import React from 'react';
import { ContentfulCarouselBanner, ProgressBarThemeType } from '@jbhi-fi/apis/src/contentful/types';
import { ContentfulImage } from '../../ContentfulImage/ContentfulImage';
import { AssetTransformer } from './AssetTransformer';
import { HeroBannerProps } from '../../HeroBanner/HeroBanner';
import { HeroBannerCarouselProps } from '../../HeroBannerCarousel/HeroBannerCarousel';
import { ThemeTransformer } from './ThemeTransformer';
import { palette } from '@jbhi-fi/lanyard-ui/themes';

export abstract class CarouselBannerTransformer {
  static toHeroBannerProps = (
    entry?: ContentfulCarouselBanner,
    options?: { isCollectionPage?: boolean }
  ): { isLoading?: false } & HeroBannerProps => {
    const useDynamicImageUrls = entry?.fields?.dynamicImageUrls;
    const hasHeroBanner = !!(useDynamicImageUrls
      ? entry?.fields?.desktopHeroBannerUrl
      : entry?.fields?.desktopHeroBanner);
    const hasLegacyBanner = !!(useDynamicImageUrls ? entry?.fields?.desktopImageUrl : entry?.fields?.desktopImage);
    const shouldUseHeroBanner = hasHeroBanner && (!hasLegacyBanner || !options?.isCollectionPage);
    return {
      href: entry?.fields?.urllink ?? '',
      isContained: shouldUseHeroBanner && entry?.fields?.hasBorder,
      isHeroBanner: shouldUseHeroBanner,
      image: useDynamicImageUrls ? (
        <img
          src={shouldUseHeroBanner ? entry?.fields?.desktopHeroBannerUrl : entry?.fields?.desktopImageUrl}
          loading="eager"
          alt="Banner"
        />
      ) : (
        <ContentfulImage
          {...AssetTransformer.toContentfulImageProps(
            shouldUseHeroBanner ? entry?.fields?.desktopHeroBanner : entry?.fields?.desktopImage
          )}
          loading="eager"
          sizes="(max-width: 1124px) 100vw, 2324px"
          isProgressive={false}
        />
      ),
      imageMobile: useDynamicImageUrls ? (
        <img
          src={shouldUseHeroBanner ? entry.fields?.mobileHeroBannerUrl : entry?.fields?.mobileImageUrl}
          loading="eager"
          alt="Banner"
        />
      ) : (
        <ContentfulImage
          {...AssetTransformer.toContentfulImageProps(
            shouldUseHeroBanner ? entry?.fields?.mobileHeroBanner : entry?.fields?.mobileImage
          )}
          loading="eager"
          sizes="(max-width: 1124px) 100vw, 2324px"
          isProgressive={false}
        />
      ),
      trackingData: {
        id: entry?.fields?.name ?? '',
        url: entry?.fields?.urllink,
        type: 'carousel',
        brand: entry?.fields?.brand,
      },
    };
  };

  static toHeroBannerCarouselProps = (
    banners: Array<{ entry: ContentfulCarouselBanner; campaignName: string; category: string }>,
    options?: { isCollectionPage?: boolean }
  ): HeroBannerCarouselProps => {
    return {
      heroBanners: banners
        .filter(banner => Boolean(banner?.entry?.fields))
        .map((banner, i) => {
          const bannerProps = CarouselBannerTransformer.toHeroBannerProps(banner.entry, options);
          return {
            ...bannerProps,
            trackingData: {
              id: '',
              ...bannerProps.trackingData,
              position: i + 1,
              campaign: banner.campaignName,
              category: banner.category,
            },
            theme: {
              background:
                ThemeTransformer.toContentThemePalette(banner.entry.fields?.backgroundColour).background ??
                palette.primary.main,
              paginationActive:
                banner.entry.fields?.progressBarTheme === ProgressBarThemeType.Dark ? palette.primary.main : undefined,
              arrow:
                banner.entry.fields?.progressBarTheme === ProgressBarThemeType.Dark ? palette.primary.main : undefined,
            },
          };
        }),
    };
  };
}
